import EmailOutlined from '@mui/icons-material/EmailOutlined';
import PhoneOutlined from '@mui/icons-material/PhoneOutlined';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';

import UserIcon from 'assets/volvoIcons/People/UserIcon';
import { EmailLink } from 'library/components/link/EmailLink';
import { PhoneNumberLink } from 'library/components/link/PhoneNumberLink';
import {
	getFullName,
	isAnonymous,
	usePhoneNumberWithPrefix,
} from 'modules/EndCustomer/Shared/Mappers';
import { LocationCountryText } from 'modules/Shared/Components/OperatingLocation/LocationCountryText';
import type { CustomerBoxProps } from 'modules/UnitOverview/types';

export const CustomerBox = ({ customer, isOwner }: CustomerBoxProps) => {
	const { t } = useTranslation('customer');
	const header = isOwner ? t('owner-label') : t('operator-label');
	const phoneNumber = usePhoneNumberWithPrefix(customer);

	const isStorageConsentGiven =
		!isAnonymous(customer?.consentApprovals ?? null) &&
		customer?.isAnonymized !== true;

	return (
		<>
			<UserIcon sx={{ display: 'block' }} />
			<Typography sx={{ display: 'block' }} variant="caption">
				{header} {!isOwner && `(${t('optional')})`}
			</Typography>
			{isOwner && customer == null && (
				<Typography
					variant="h3"
					sx={{
						color: 'grey.400',
					}}>
					{t('not-assigned')}
				</Typography>
			)}
			{!isOwner && customer == null && (
				<Typography
					variant="body2"
					sx={{
						color: 'grey.400',
					}}>
					{t('unit-manager-description')}
				</Typography>
			)}
			{customer && (
				<Box
					sx={{
						mt: 0.5,
					}}>
					<Box>
						<Typography component="p" variant="h6">
							{isStorageConsentGiven
								? getFullName(customer)
								: t('anonymous')}
						</Typography>
						{customer.companyName != null && (
							<Typography component="p" variant="body1">
								{isStorageConsentGiven && customer.companyName}
							</Typography>
						)}
						<Typography
							component="p"
							variant="body1"
							sx={{
								textOverflow: 'ellipsis',
								overflow: 'hidden',
							}}>
							<EmailOutlined
								fontSize="small"
								sx={{
									mr: 0.5,
									verticalAlign: 'bottom',
								}}
							/>
							{isStorageConsentGiven && (
								<EmailLink email={customer.email} />
							)}
						</Typography>
						<Typography
							component="p"
							variant="body1"
							sx={{
								textOverflow: 'ellipsis',
								overflow: 'hidden',
							}}>
							<PhoneOutlined
								fontSize="small"
								sx={{
									mr: 0.5,
									verticalAlign: 'bottom',
								}}
							/>
							<PhoneNumberLink phoneNumber={phoneNumber} />
						</Typography>
						<Typography component="p" variant="body1">
							{isStorageConsentGiven && customer.addressFirstPart}
						</Typography>
						<Typography component="p" variant="body1">
							{isStorageConsentGiven &&
								customer.addressSecondPart}
						</Typography>
					</Box>
					<Typography component="p" variant="body1">
						{customer.postalCode} {customer.city}
					</Typography>
					<Typography component="p" variant="body1">
						<LocationCountryText
							countryCode={customer.countryCode}
						/>
					</Typography>
				</Box>
			)}
		</>
	);
};
